import { PropsWithChildren, ReactNode, useEffect, useRef, useState } from 'react'
import { InputText } from 'primereact/inputtext'
import { Button } from 'primereact/button'
import { MenuItem } from 'primereact/menuitem'

import { useKeyboard } from '../../hooks/keyboard'
import { useUserContext } from '../contexts/UserContext'
import { getMenuItemExport, getMenuItemViewQr } from '../common/menu-items'
import ArchivedStateLabel from '../common/pure/ArchivedStateLabel'
import ContextHeader from '../common/ContextHeader'
import { Column, Row } from '../common/pure/FlexContainer'
import PrimeIcon, { pi } from '../common/pure/PrimeIcon'
import { LayoutColumnContainer } from '../common/pure/layout'
import Breadcrumbs from '../common/Breadcrumbs'
import TooltipWrapper from '../common/pure/TooltipWrapper'
import QrDialog from '../dialogs/QrDialog'
import BoltConnection from '../../models/BoltConnection'
import Project from '../../models/Project'
import Bolt from '../../models/Bolt'
import { Ancestor } from '../../models/ApiStructureModel'
import DirectoryEntry from '../../models/DirectoryEntry'
import MenuBuilder from '../../tools/MenuBuilder'
import t from '../../tools/t'

interface StructureScreenProps extends PropsWithChildren {
    node?: Project | BoltConnection | Bolt
    parent?: DirectoryEntry | Project | BoltConnection
    ancestors: Ancestor[]
    nodeHasChanged?: boolean
    menu?: MenuItem[]
    qr?: string
    customActionTray?: ReactNode

    // NOTE: These actions can be hidden by omitting their respective handler.
    onSubmitNameChange?: (name: string) => Promise<boolean>
    onSaveChanges?: () => void
    onRestore?: () => void
    onExport?: () => void
}

export default function StructureScreen({
    children,
    node,
    parent,
    ancestors,
    onSubmitNameChange,
    onSaveChanges,
    onRestore,
    onExport,
    nodeHasChanged,
    menu,
    qr,
    customActionTray,
}: StructureScreenProps) {
    const { me } = useUserContext()
    const [editable, setEditable] = useState(false)
    const [name, setName] = useState('')
    const [error, setError] = useState(false)
    const [qrVisible, setQrVisible] = useState(false)
    const inputRef = useRef<HTMLInputElement>(null)
    const isArchived = !!node?.isArchived || !!parent?.isArchived

    const startEditing = () => {
        // NOTE: Inline title editing is predicated on a change handler prop existing.
        if (!onSubmitNameChange) return
        if (isArchived) return
        if (editable) return
        setEditable(true)
    }

    const cancelEditing = () => {
        if (!editable) return
        setEditable(false)
        if (node) setName(node.name)
        else setName('')
    }

    const finishEditing = async () => {
        if (!(editable && onSubmitNameChange)) return
        if (isArchived) return
        const sanitizedName = name.trim()
        if (!sanitizedName) return
        setEditable(false)
        // Remove url search query to avoid reading the ?new query again.
        if (location.search.includes('?new'))
            history.replaceState({}, document.title, location.pathname)
        const success = await onSubmitNameChange(sanitizedName)
        if (success) setName(sanitizedName)
        else setName(node?.name ?? '')
    }

    useKeyboard('Enter', finishEditing, [finishEditing])
    useKeyboard('Escape', cancelEditing, [cancelEditing])

    useEffect(() => {
        setName(node?.name ?? '')
        if (node) {
            const isNew = location.search.includes('new=' + node?.id)
            setEditable(isNew && !isArchived)
        } else setError(true)
    }, [node, isArchived])

    useEffect(() => {
        const input = inputRef.current
        if (input && editable) input.focus()
    }, [inputRef, editable])

    const menuItems = new MenuBuilder()
        .addIf(
            getMenuItemExport(onExport, t('common_export_x', node?.name ?? '')),
            !!onExport && !!node
        )
        .addIf(
            getMenuItemViewQr(() => setQrVisible(true)),
            !!qr && !!node
        )
        .addIf(
            {
                label: t('structure_node_rename'),
                icon: pi('pencil'),
                command: startEditing,
            },
            !!onSubmitNameChange && !isArchived
        )
        .concat(menu)
        .toArray()

    return (
        <LayoutColumnContainer grow={false} className="structure-screen">
            {!error && (
                <Column gap={0.5} className="structure-screen__header">
                    {isArchived ? (
                        <ArchivedStateLabel onRestore={onRestore} />
                    ) : (
                        <Breadcrumbs node={node} ancestors={ancestors} />
                    )}
                    <Row
                        alignment="center"
                        arrangement="spaced"
                        className="structure-screen__name-row"
                    >
                        <Row gap={2} alignment="center">
                            <ContextHeader menu={menuItems}>
                                <Row gap={2} alignment="center" relative>
                                    {editable ? (
                                        <>
                                            <InputText
                                                ref={inputRef}
                                                className="structure-screen__input"
                                                value={name}
                                                onChange={(e) => setName(e.target.value)}
                                            />
                                            <PrimeIcon
                                                name="check-circle"
                                                className="structure-screen__submit-name"
                                                onClick={finishEditing}
                                            />
                                        </>
                                    ) : (
                                        <div
                                            onDoubleClick={startEditing}
                                            className="structure-screen__title"
                                        >
                                            <TooltipWrapper
                                                label={name}
                                                position="bottom"
                                                target="structure-name"
                                                disabled={name.length < 100}
                                            >
                                                {name}
                                            </TooltipWrapper>
                                        </div>
                                    )}
                                </Row>
                            </ContextHeader>
                        </Row>

                        <Row gap={0.5} alignment="center">
                            {onSaveChanges && (
                                <Button
                                    icon={pi('save')}
                                    disabled={isArchived || !nodeHasChanged}
                                    label={t('common_save_all')}
                                    onClick={onSaveChanges}
                                />
                            )}
                            {customActionTray}
                        </Row>
                    </Row>
                    {me?.isAdmin && <Row className="structure-screen__id">id: {node?.id}</Row>}
                </Column>
            )}
            {children}
            {qr && (
                <QrDialog
                    draggable={false}
                    name={node?.name}
                    qr={qr}
                    visible={qrVisible}
                    onHide={() => setQrVisible(false)}
                />
            )}
        </LayoutColumnContainer>
    )
}
